import {
  DeleteOutlined,
  EditOutlined,
  FolderAddOutlined,
  PlusOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { Button, Divider, Popover, Spin, Tooltip } from "antd";
import { Content } from "antd/lib/layout/layout";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import DeleteOrgLevelApi from "../../api/org-levels/DeleteOrgLevelApi";
import { GetOrganizationLevelsApi } from "../../api/org-levels/GetOrganizationLevelsApi";
import DashboardBreadcrumb from "../../components/layout/DashboardBreadcrumb";
import AddOrgLevelModal from "../../components/modal/add/AddOrgLevelModal";
import ConfirmationModal from "../../components/modal/ConfirmationModal";
import EditOrgLevelModal from "../../components/modal/edit/EditOrgLevelModal";
import PageLoader from "../../components/sub/PageLoader";
import {
  openNotificationError,
  openNotificationSuccess,
} from "../../helpers/NotificationHelpers";
import { permissionAbility } from "../../helpers/permissionHelpers";

function OrgLevels() {
  const [loading, setLoading] = useState(false);
  const [topLevel, setTopLevel] = useState();
  const [addModal, setAddModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState("");
  const [levels, setLevels] = useState();
  const [newOrder, setNewOrder] = useState();
  const [editLevel, setEditLevel] = useState();
  const [deleteLevelId, setDeleteLevelId] = useState("");
  const proHeight = document.body.clientHeight;
  const positionState = useSelector((state) => state.positionState.value);
  const userLevel = positionState?.userRole?.name;

  useEffect(() => {
    getOrganizationLevels(1);
  }, []);

  async function getOrganizationLevels() {
    setLoading(true);
    const apiResponse = await GetOrganizationLevelsApi();
    if (apiResponse.message === "success") {
      setLevels(apiResponse.levels);
      setLoading(false);
      apiResponse.levels?.forEach((level) => {
        if (level.order === 1) {
          setTopLevel(level);
        }
      });
    }
  }

  async function deleteOrgLevel() {
    const apiResponse = await DeleteOrgLevelApi(deleteLevelId);
    if (apiResponse.statusCode) {
      openNotificationError("bottomRight", apiResponse.message);
    } else if (apiResponse.message === "success") {
      openNotificationSuccess("bottomRight", "Organization deleted");
      setDeleteLevelId("");
      getOrganizationLevels();
    } else {
      openNotificationError("bottomRight", "Couldn't delete. Try again later.");
    }
  }

  const right = (
    <>
      {permissionAbility(userLevel, "org-level", "add") && (
        <Tooltip placement="left" title={"Add Org Level"}>
          <Button
            onClick={() => {
              setAddModal(true);
              setNewOrder(levels.length + 1);
            }}
            shape="circle"
            className="ml-3"
            type="primary"
            icon={<PlusOutlined />}
          ></Button>
        </Tooltip>
      )}
    </>
  );

  const left = <div className="text-lg pri-font">Organization Levels</div>;

  const deleteMessage = (
    <div className="text-xs w-48">
      Can't delete this level. This level have active units.
    </div>
  );

  function getTopLevel() {
    return (
      <div className="flex items-center mb-2">
        <div className="w-5"></div>
        <div className="border border-gray-300 rounded p-3 w-full flex justify-between items-center">
          <div className="flex">
            <div className="mr-2 flex items-center">
              {topLevel?.order || "1"}
            </div>
            <div
              style={{ borderColor: "#d9d9d9" }}
              className="border p-1 rounded mr-2"
            >
              <div
                style={{ backgroundColor: topLevel?.color || "#fff" }}
                className="w-6 h-full"
              ></div>
            </div>
            <div className="py-1">{topLevel?.name || "..."}</div>
          </div>
          {permissionAbility(userLevel, "org-level", "edit") && (
            <Tooltip title={"Edit Level"}>
              <div
                onClick={() => {
                  setEditModal(true);
                  setEditLevel(topLevel);
                }}
                className="cursor-pointer text-blue-500 hover:text-blue-600"
              >
                <EditOutlined />
              </div>
            </Tooltip>
          )}
        </div>
        <div className="w-5"></div>
      </div>
    );
  }

  function getLevels() {
    let output = [];

    levels.sort((a, b) => {
      return a.order - b.order;
    });

    levels?.forEach((level) => {
      if (level.order != 1) {
        output.push(
          <div key={level.order}>
            <div className="flex items-center" style={{ marginTop: '10px', marginBottom: '10px' }}>
              <div className="w-5"></div>
              <div className="border border-gray-300 rounded p-3 w-full flex justify-between">
                <div className="flex">
                  <div className="mr-2 flex items-center">{level.order}</div>
                  <div
                    style={{ borderColor: "#d9d9d9" }}
                    className="border p-1 rounded mr-2"
                  >
                    <div
                      style={{ backgroundColor: level.color }}
                      className="w-6 h-full"
                    ></div>
                  </div>
                  <div className="py-1">{level.name}</div>
                </div>
                <div className="flex">
                  {permissionAbility(userLevel, "org-level", "edit") && (
                    <Tooltip title={"Edit Level"}>
                      <div
                        onClick={() => {
                          setEditModal(true);
                          setEditLevel(level);
                        }}
                        className="mr-3 cursor-pointer text-blue-500 hover:text-blue-600"
                      >
                        <EditOutlined />
                      </div>
                    </Tooltip>
                  )}
                  {permissionAbility(userLevel, "org-level", "delete") &&
                    (level.canDelete ? (
                      <Tooltip title={"Delete"}>
                        <div
                          onClick={() => {
                            setDeleteModal("delete");
                            setDeleteLevelId(level.id);
                          }}
                          className="text-red-500 hover:text-red-600 cursor-pointer"
                        >
                          <DeleteOutlined />
                        </div>
                      </Tooltip>
                    ) : level.canArchive ? (
                      <Tooltip title={"Archive"}>
                        <div
                          onClick={() => {
                            setDeleteModal("archive");
                            setDeleteLevelId(level.id);
                          }}
                          className="text-red-500 hover:text-red-600 cursor-pointer"
                        >
                          <FolderAddOutlined />
                        </div>
                      </Tooltip>
                    ) : (
                      <Popover
                        placement="left"
                        content={deleteMessage}
                        trigger="hover"
                      >
                        <div className="text-gray-500">
                          <FolderAddOutlined />
                        </div>
                      </Popover>
                    ))}
                </div>
              </div>
              <div className="w-5"></div>
            </div>
            <div className="px-5">
              {permissionAbility(userLevel, "org-level", "add") ? (
                <button
                  onClick={() => {
                    setAddModal(true);
                    setNewOrder(level.order + 1);
                  }}
                  className="my-1 border hover:border-gray-500 border-dashed rounded cursor-pointer text-center w-full duration-300 text-gray-300 hover:text-gray-500"
                >
                  +
                </button>
              ) : (
                <div className="py-2"></div>
              )}
            </div>
          </div>
        );
      }
    });

    return output;
  }

  return (
    <>
      <Content style={{ backgroundColor: "#fff" }}>
        <DashboardBreadcrumb left={left} right={right} />
        <div
          style={{
            height: proHeight - 122,
            overflow: "auto",
          }}
          className="custom-scroll p-6"
        >
          {levels ? (
            <>
              {levels?.length ? (
                <Spin spinning={loading}>
                  <div className="max-w-md mx-auto" style={{ marginBottom: '10px' }}>
                    <div className="py-3 px-2" style={{ backgroundColor: 'rgba(245, 235, 220, 0.4)', border: '1px solid rgba(241, 90, 36, 0.38)', borderRadius: '8px' }}>
                      <div className="text-center mb-3">Top level</div>
                      {getTopLevel()}
                    </div>
                    {permissionAbility(userLevel, "org-level", "add") ? (
                      <div className="px-5">
                        <button
                          onClick={() => {
                            setAddModal(true);
                            setNewOrder(2);
                          }}
                          className="my-1 border hover:border-gray-500 border-dashed rounded cursor-pointer text-center w-full duration-300 text-gray-300 hover:text-gray-500"
                        >
                          +
                        </button>
                      </div>
                    ) : (
                      <div className="py-2"></div>
                    )}
                  </div>
                  <div className="max-w-md mx-auto">
                    {getLevels()}
                    <Divider
                      style={{ borderColor: "#e5e7eb", color: "#d5d7db" }}
                      plain
                    >
                      Bottom Level
                    </Divider>
                  </div>
                </Spin>
              ) : (
                <div className="flex justify-center items-center w-full h-full">
                  <div className="text-center">
                    <div className="text-3xl text-gray-400">
                      <WarningOutlined />
                    </div>
                    <div className="text-xl pri-font text-gray-400">
                      No levels available
                    </div>
                    {permissionAbility(userLevel, "org-level", "add") && (
                      <>
                        <div className="mb-3 text-gray-400">
                          Add different levels in your organization
                        </div>
                        <Button
                          onClick={() => {
                            setAddModal(true);
                            setNewOrder(1);
                          }}
                          type="primary"
                        >
                          Add a level
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              )}
            </>
          ) : (
            <PageLoader />
          )}
        </div>
      </Content>
      <AddOrgLevelModal
        visible={addModal}
        handleCancel={() => setAddModal(false)}
        handleOk={() => getOrganizationLevels()}
        levels={levels}
        order={newOrder}
      />
      <EditOrgLevelModal
        visible={editModal}
        handleCancel={() => setEditModal(false)}
        handleOk={() => {
          getOrganizationLevels();
          setEditLevel();
        }}
        organization={editLevel}
      />
      <ConfirmationModal
        visible={deleteModal}
        handleCancel={() => setDeleteModal("")}
        handleOk={() => {
          deleteOrgLevel();
          setDeleteLevelId("");
        }}
        text={`Are you sure want to ${deleteModal} this org level?`}
        title={deleteModal?.charAt(0).toUpperCase() + deleteModal?.slice(1)}
      />
    </>
  );
}

export default OrgLevels;
